// ── Import shared utilities for local use and re-export ────────────────────
import {
  aggregateManufacturerYearlyData,
  calcClaimsGrowthPct,
  calcInsuredSpendPct,
  calcSharePct,
  findTopAgeCohort,
  formatCount,
  formatQuarterLabel,
  formatSpend,
  getTopEntries,
  parseAgeCohorts,
  parseGenderTotals,
  PAY_LABEL,
  sumClaimsByNames,
} from '$lib/utils/report/calculations'
import type {
  GenderTotals,
  QuarterData,
  StudyStats,
  YearlyAggregates,
} from '$lib/utils/report/calculations'

// ── ADC Oncology Revolution study — drug/manufacturer constants ────────────

export const ASTRAZENECA_DAIICHI_DRUGS = ['ENHERTU'] as const

export const PFIZER_SEAGEN_DRUGS = ['ADCETRIS', 'PADCEV', 'TIVDAK', 'TUKYSA'] as const

export const ROCHE_GENENTECH_DRUGS = ['KADCYLA', 'POLIVY'] as const

export const GILEAD_DRUGS = ['TRODELVY'] as const

export const ABBVIE_DRUGS = ['ELAHERE'] as const

export {
  PAY_LABEL,
  aggregateManufacturerYearlyData,
  parseGenderTotals,
  parseAgeCohorts,
  calcClaimsGrowthPct,
  getTopEntries,
  formatQuarterLabel,
  formatCount,
  formatSpend,
  sumClaimsByNames,
  calcSharePct,
  calcInsuredSpendPct,
  findTopAgeCohort,
}

export type { QuarterData, GenderTotals, YearlyAggregates, StudyStats }

// ── Study-specific display types ───────────────────────────────────────────

/** Subset of page data needed for display stat computation. */
export interface AdcStudyPageData {
  quarterlyData: Record<string, QuarterData>
  topDrugs: [string, number][]
  payerData: [string, number][]
  genderTotals: [number, number, number]
  ageCohorts: [string, number][]
  stats: StudyStats
}

export interface DisplayStats {
  quarterCount: number
  firstQtrLabel: string
  latestQtrLabel: string
  quarterLabels: string[]
  firstQtrClaimsK: string
  latestQtrClaimsK: string
  totalClaimsK: string
  totalSpendM: string
  growthPct: string
  latestQtrM: string
  baselineClaimsK: string
  baselineSpendM: string
  maxDrug: number
  maxPay: number
  azdClaimsK: string
  azdSharePct: string
  pfizerClaimsK: string
  pfizerSharePct: string
  azdClaimsTotal: number
  pfizerClaimsTotal: number
  insuredSpendPct: string
  totalGen: number
  femalePct: string
  malePct: string
  unknownPct: string
  topAge: [string, number]
  topAgeLabel: string
  topAgeK: string
}

/**
 * Derives all display-ready values used across slides from the study page
 * data and a pre-sorted list of quarter keys.  Pure function — no side
 * effects, no Svelte dependencies.
 */
export function calcDisplayStats(
  data: AdcStudyPageData,
  sortedQuarterKeys: string[],
): DisplayStats {
  const hasQuarters = sortedQuarterKeys.length > 0
  const firstQuarter = hasQuarters ? data.quarterlyData[sortedQuarterKeys[0]] : undefined
  const lastQuarter = hasQuarters
    ? data.quarterlyData[sortedQuarterKeys[sortedQuarterKeys.length - 1]]
    : undefined
  const quarterLabels = sortedQuarterKeys.map(formatQuarterLabel)

  const totalDrugs = data.topDrugs.reduce((s, d) => s + d[1], 0)
  const totalPayVol = data.payerData.reduce((s, p) => s + p[1], 0)

  const azdClaims = sumClaimsByNames(data.topDrugs, ASTRAZENECA_DAIICHI_DRUGS)
  const pfizerClaims = sumClaimsByNames(data.topDrugs, PFIZER_SEAGEN_DRUGS)

  const totalGen = data.genderTotals.reduce((a, b) => a + b, 0)
  const topAge = findTopAgeCohort(data.ageCohorts)

  const cashPay = data.payerData.find((p) => p[0] === 'Cash Pay')?.[1] ?? 0

  return {
    quarterCount: sortedQuarterKeys.length,
    firstQtrLabel: hasQuarters ? formatQuarterLabel(sortedQuarterKeys[0]) : '',
    latestQtrLabel: hasQuarters
      ? formatQuarterLabel(sortedQuarterKeys[sortedQuarterKeys.length - 1])
      : '',
    quarterLabels,

    firstQtrClaimsK: formatCount(firstQuarter?.claims ?? 0),
    latestQtrClaimsK: formatCount(lastQuarter?.claims ?? 0),
    totalClaimsK: formatCount(data.stats.totalClaims),
    totalSpendM: formatSpend(data.stats.totalSpend),
    growthPct: Math.round(data.stats.claimsGrowthPct).toLocaleString(),
    latestQtrM: formatSpend(data.stats.latestQuarterSpend),

    baselineClaimsK: formatCount(firstQuarter?.claims ?? 0),
    baselineSpendM: formatSpend(firstQuarter?.total_pay ?? 0),

    maxDrug: data.topDrugs[0]?.[1] ?? 1,
    maxPay: data.payerData[0]?.[1] ?? 1,

    azdClaimsK: formatCount(azdClaims),
    azdSharePct: calcSharePct(azdClaims, totalDrugs).toFixed(1),
    pfizerClaimsK: formatCount(pfizerClaims),
    pfizerSharePct: calcSharePct(pfizerClaims, totalDrugs).toFixed(1),
    azdClaimsTotal: azdClaims,
    pfizerClaimsTotal: pfizerClaims,

    insuredSpendPct: calcInsuredSpendPct(cashPay, totalPayVol).toFixed(1),

    totalGen,
    ...((): { femalePct: string; malePct: string; unknownPct: string } => {
      const f = calcSharePct(data.genderTotals[0], totalGen)
      const m = calcSharePct(data.genderTotals[1], totalGen)
      const femalePct = f.toFixed(1)
      const malePct = m.toFixed(1)
      // Third as remainder so Female + Male + Unknown always display as 100%
      const unknownPct = (100 - parseFloat(femalePct) - parseFloat(malePct)).toFixed(1)
      return { femalePct, malePct, unknownPct }
    })(),

    topAge,
    topAgeLabel: topAge[0],
    topAgeK: formatCount(topAge[1]),
  }
}
